import { Injectable, NgZone } from '@angular/core';

/** What kind of picker to open and what it should accept. */
export interface FilePickOptions {
  /**
   * Open the device camera directly (`capture="environment"`). Mobile only —
   * on desktop this resolves to an ordinary file dialog, so callers should pass
   * false there rather than offering a camera button that does nothing useful.
   */
  camera?: boolean;
  /** Allow selecting several files at once. */
  multiple?: boolean;
  /** Accept PDFs alongside images — the upload paths take a scanned slip. */
  allowPdf?: boolean;
}

/**
 * Handles a picked selection. The picker keeps the underlying <input> alive
 * until the returned promise settles, so async reads of the File objects are
 * safe (see `pick`).
 */
export type FilePickHandler = (files: File[]) => Promise<void> | void;

/**
 * Opens a transient `<input type="file">` and hands the chosen Files to a
 * caller-supplied handler.
 *
 * This exists because the receiving flow had three near-identical copies of this
 * code (packing-slip capture, shipment photos, damaged-item photos), each
 * carrying its own copy of two non-obvious mobile workarounds. Both now live
 * here, once:
 *
 *  1. The input is appended to the DOM (off-screen). iOS Safari often will not
 *     fire `change` for a native-camera capture unless the element is connected.
 *  2. The input is not removed until the handler's promise settles. A camera File
 *     is backed by the input element, so tearing it down early makes reads of
 *     that File fail.
 *
 * The handler runs inside Angular's zone: returning from the native camera can
 * fire `change` outside it, which otherwise leaves the caller's thumbnails
 * un-rendered until some later tick.
 *
 * The `.click()` happens synchronously inside `pick()`, so callers must invoke it
 * directly from the user's tap/click handler — an awaited step before it lets
 * mobile browsers suppress the picker as untrusted.
 */
@Injectable({ providedIn: 'root' })
export class FilePickerService {

  constructor(private _zone: NgZone) {}

  /**
   * Opens the picker. Does nothing further if the user cancels; the handler only
   * runs when at least one file was chosen.
   */
  public pick(options: FilePickOptions, handler: FilePickHandler): void {
    const input = this._makeInput(options);

    input.onchange = (event) => {
      const picked = (event.target as HTMLInputElement).files;
      if (!picked || picked.length === 0) {
        this._cleanup(input);
        return;
      }
      const files = Array.from(picked);
      this._zone.run(() => {
        Promise.resolve()
          .then(() => handler(files))
          .catch(() => { /* handlers own their own error reporting */ })
          .finally(() => this._cleanup(input));
      });
    };

    input.oncancel = () => this._cleanup(input);
    input.click();
  }

  /**
   * Filters a drag-and-drop payload down to the file types the picker would have
   * accepted, so the drop path and the dialog path agree on what's allowed.
   * Rejected names come back separately for the caller to report.
   */
  public filterDropped(
    files: File[],
    options: Pick<FilePickOptions, 'allowPdf'>,
  ): { accepted: File[]; rejected: string[] } {
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of files) {
      const ok = file.type.startsWith('image/')
        || (!!options.allowPdf && file.type === 'application/pdf');
      if (ok) { accepted.push(file); } else { rejected.push(file.name); }
    }
    return { accepted, rejected };
  }

  private _makeInput(options: FilePickOptions): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = options.allowPdf ? 'image/*,application/pdf' : 'image/*';
    if (options.camera) { input.capture = 'environment'; }
    if (options.multiple) { input.multiple = true; }
    // Workaround (1) — see the class comment.
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.opacity = '0';
    input.setAttribute('aria-hidden', 'true');
    document.body.appendChild(input);
    return input;
  }

  private _cleanup(input: HTMLInputElement): void {
    input.value = '';
    input.remove();
  }
}
